import React from 'react';
import { UseFormRegister, FieldErrors } from 'react-hook-form';
import { Employee, Car } from '../../../types';

interface CrewCertificateFormData {
  documentCategory: 'employee' | 'car';
  employeeId: string;
  carId: string;
  courseType: string;
  profession: string;
  issueDate: string;
  expiryDate: string;
  certificateNumber: string;
  capacity: string;
  limitation: string;
  certificateFile?: FileList;
  [key: string]: any;
}

interface DocumentFormFieldsProps {
  register: UseFormRegister<CrewCertificateFormData>;
  errors: FieldErrors<CrewCertificateFormData>;
  documentCategory: 'employee' | 'car';
  employeeOptions: { value: string; label: string }[];
  carOptions: { value: string; label: string }[];
  documentTypeOptions: { value: string; label: string }[];
  professionOptions: { value: string; label: string }[];
  selectedEmployee: Employee | null;
  selectedCar: Car | null;
}

const DocumentFormFields: React.FC<DocumentFormFieldsProps> = ({
  register,
  errors,
  documentCategory,
  employeeOptions,
  carOptions,
  documentTypeOptions,
  professionOptions,
  selectedEmployee,
  selectedCar,
}) => {
  return (
    <div className="overflow-hidden bg-white border border-gray-200 rounded-lg">
      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">
          {documentCategory === 'employee' ? 'Employee Document Information' : 'Car Document Information'}
        </h3>
      </div>
      
      <div className="divide-y divide-gray-200">
        {documentCategory === 'employee' && (
          <div className="px-4 py-4 flex items-center justify-between">
            <div className="w-1/3">
              <label className="text-sm font-medium text-gray-700">Employee (الموظف)</label>
            </div>
            <div className="w-2/3">
              <select
                {...register('employeeId')}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select Employee...</option>
                {employeeOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.employeeId?.message && (
                <p className="mt-1 text-sm text-red-600">{errors.employeeId.message}</p>
              )}
              {selectedEmployee && (
                <div className="mt-1 text-xs text-gray-500">
                  Code: {selectedEmployee.code} | Rank: {selectedEmployee.rank}
                </div>
              )}
            </div>
          </div>
        )}

        {documentCategory === 'car' && (
          <div className="px-4 py-4 flex items-center justify-between">
            <div className="w-1/3">
              <label className="text-sm font-medium text-gray-700">Car (السيارة)</label>
            </div>
            <div className="w-2/3">
              <select
                {...register('carId')}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select Car...</option>
                {carOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.carId?.message && (
                <p className="mt-1 text-sm text-red-600">{errors.carId.message}</p>
              )}
              {selectedCar && (
                <div className="mt-1 text-xs text-gray-500">
                  Code: {selectedCar.code} | Chassis: {selectedCar.chassisNumber}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="px-4 py-4 flex items-center justify-between">
          <div className="w-1/3">
            <label className="text-sm font-medium text-gray-700">Document Type (نوع الوثيقة)</label>
          </div>
          <div className="w-2/3">
            <select
              {...register('courseType')}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Select Document Type...</option>
              {documentTypeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {errors.courseType?.message && (
              <p className="mt-1 text-sm text-red-600">{errors.courseType.message}</p>
            )}
          </div>
        </div>

        {documentCategory === 'employee' && (
          <div className="px-4 py-4 flex items-center justify-between">
            <div className="w-1/3">
              <label className="text-sm font-medium text-gray-700">Profession (المهنة)</label>
            </div>
            <div className="w-2/3">
              <select
                {...register('profession')}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select Profession...</option>
                {professionOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.profession?.message && (
                <p className="mt-1 text-sm text-red-600">{errors.profession.message}</p>
              )}
            </div>
          </div>
        )}

        <div className="px-4 py-4 flex items-center justify-between">
          <div className="w-1/3">
            <label className="text-sm font-medium text-gray-700">Document Number (رقم الوثيقة)</label>
          </div>
          <div className="w-2/3">
            <input
              {...register('certificateNumber')}
              placeholder={documentCategory === 'employee' ? 'CERT001' : 'DOC001'}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
            {errors.certificateNumber?.message && (
              <p className="mt-1 text-sm text-red-600">{errors.certificateNumber.message}</p>
            )}
          </div>
        </div>

        {documentCategory === 'employee' && (
          <>
            <div className="px-4 py-4 flex items-center justify-between">
              <div className="w-1/3">
                <label className="text-sm font-medium text-gray-700">Capacity (الصفة)</label>
              </div>
              <div className="w-2/3">
                <input
                  {...register('capacity')}
                  placeholder="Officer in Charge"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
                {errors.capacity?.message && (
                  <p className="mt-1 text-sm text-red-600">{errors.capacity.message}</p>
                )}
              </div>
            </div>

            <div className="px-4 py-4 flex items-center justify-between">
              <div className="w-1/3">
                <label className="text-sm font-medium text-gray-700">Limitation (القيود)</label>
              </div>
              <div className="w-2/3">
                <input
                  {...register('limitation')}
                  placeholder="None"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
                {errors.limitation?.message && (
                  <p className="mt-1 text-sm text-red-600">{errors.limitation.message}</p>
                )}
              </div>
            </div>
          </>
        )}

        <div className="px-4 py-4 flex items-center justify-between">
          <div className="w-1/3">
            <label className="text-sm font-medium text-gray-700">Issue Date</label>
          </div>
          <div className="w-2/3">
            <input
              type="date"
              {...register('issueDate')}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
            {errors.issueDate?.message && (
              <p className="mt-1 text-sm text-red-600">{errors.issueDate.message}</p>
            )}
          </div>
        </div>

        <div className="px-4 py-4 flex items-center justify-between">
          <div className="w-1/3">
            <label className="text-sm font-medium text-gray-700">Expiry Date</label>
          </div>
          <div className="w-2/3">
            <input
              type="date"
              {...register('expiryDate')}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
            {errors.expiryDate?.message && (
              <p className="mt-1 text-sm text-red-600">{errors.expiryDate.message}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentFormFields;
